import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  ScrollText,
  Radio,
  Mail,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Menu,
  X
} from 'lucide-react';

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
}

const SIDEBAR_KEY = 'admin-sidebar-collapsed';

const navItems: NavItem[] = [
  { name: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" />, path: '/admin' },
  { name: 'Users', icon: <Users className="w-5 h-5" />, path: '/admin/users' },
  { name: 'Statistics', icon: <BarChart3 className="w-5 h-5" />, path: '/admin/statistics' },
  { name: 'Audit Log', icon: <ScrollText className="w-5 h-5" />, path: '/admin/audit' },
  { name: 'Webhook Logs', icon: <Radio className="w-5 h-5" />, path: '/admin/webhooks' },
  { name: 'Email Templates', icon: <Mail className="w-5 h-5" />, path: '/admin/emails' },
];

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === 'true'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(collapsed)); } catch {}
  }, [collapsed]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const sidebarContent = (
    <>
      {/* Header */}
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-4 py-5 border-b border-gray-200`}>
        {!collapsed && (
          <h2 className="text-lg font-bold text-gray-900">Admin</h2>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`
                  group flex items-center ${collapsed ? 'justify-center' : ''} px-3 py-2.5 text-sm font-medium rounded-lg transition-all
                  ${isActive(item.path)
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                title={collapsed ? item.name : undefined}
              >
                <span className={isActive(item.path) ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-500'}>
                  {item.icon}
                </span>
                {!collapsed && <span className="ml-3">{item.name}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Back to App */}
      <div className="px-3 py-4 border-t border-gray-200">
        <Link
          to="/dashboard"
          className={`flex items-center ${collapsed ? 'justify-center' : ''} px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all`}
          title={collapsed ? 'Back to App' : undefined}
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
          {!collapsed && <span className="ml-3">Back to App</span>}
        </Link>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <div className={`hidden md:flex md:flex-col ${collapsed ? 'md:w-16' : 'md:w-60'} transition-all duration-200`}>
        <div className="flex flex-col flex-grow overflow-y-auto bg-white border-r border-gray-200">
          {sidebarContent}
        </div>
      </div>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-14">
          <h2 className="text-lg font-bold text-gray-900">Admin</h2>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-gray-600 bg-opacity-75" onClick={() => setMobileOpen(false)}>
          <div className="fixed inset-y-0 left-0 max-w-xs w-full bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col h-full pt-14">
              {sidebarContent}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto bg-gray-50 pt-14 md:pt-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
